import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../store';
import { CATEGORY_EMOJI } from '../types';
import ObligationCard from './ObligationCard';
import ProjectCard from './ProjectCard';
import PropertyForm from './PropertyForm';
import AttachmentsSection from './AttachmentsSection';
import ExpenseSection from './ExpenseSection';
import * as db from '../lib/db';
import { sortProjectsByTimeframe } from '../lib/projects';
import { formatUSD } from '../lib/format';

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const { properties, obligations, remindersByObligation, projects, refresh } = useAppData();
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  const property = properties.find((p) => p.id === propertyId);
  if (!property) return <div className="empty-state">Property not found.</div>;

  const propertyObligations = obligations
    .filter((o) => o.property_id === property.id && o.active)
    .sort((a, b) => (a.due_date < b.due_date ? -1 : 1));

  const propertyProjects = sortProjectsByTimeframe(
    projects.filter((p) => p.property_id === property.id)
  );

  const totalDue = propertyObligations.reduce((sum, o) => sum + (o.amount ?? 0), 0);

  async function handleDelete() {
    if (!property) return;
    if (!confirm(`Delete ${property.name}? Its obligations will be kept but unlinked.`)) return;
    await db.deleteProperty(property.id);
    await refresh();
    navigate('/properties');
  }

  if (editing) {
    return (
      <PropertyForm
        existing={property}
        onCancel={() => setEditing(false)}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="property-detail">
      <Link to="/properties" className="back-link">
        ← All Properties
      </Link>

      {property.photo_url && (
        <img className="property-hero" src={property.photo_url} alt={property.name} />
      )}

      <div className="page-header">
        <h1>
          {!property.photo_url && <>{CATEGORY_EMOJI[property.category]} </>}
          {property.name}
        </h1>
        <div className="page-header-actions">
          <button className="btn-secondary" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn-secondary danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      {property.llc_name && <p className="muted">{property.llc_name}</p>}
      {property.description && <p>{property.description}</p>}
      {property.location && <p className="muted">📍 {property.location}</p>}
      {property.notes && <p className="notes">{property.notes}</p>}

      <ExpenseSection propertyId={property.id} />

      <div className="section-header">
        <h2>
          Obligations
          {totalDue > 0 && <span className="section-total"> · {formatUSD(totalDue)} due</span>}
        </h2>
        <Link to="/obligations/new" state={{ propertyId: property.id }} className="btn-primary small">
          + Add
        </Link>
      </div>

      {propertyObligations.length === 0 ? (
        <p className="muted empty">Nothing tracked for {property.name} yet.</p>
      ) : (
        <div className="card-list">
          {propertyObligations.map((o) => (
            <ObligationCard key={o.id} obligation={o} reminders={remindersByObligation[o.id]} />
          ))}
        </div>
      )}

      <div className="section-header">
        <h2>Projects</h2>
        <Link to="/projects/new" state={{ propertyId: property.id }} className="btn-primary small">
          + Add
        </Link>
      </div>

      {propertyProjects.length === 0 ? (
        <p className="muted empty">No projects for {property.name} yet.</p>
      ) : (
        <div className="card-list">
          {propertyProjects.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}

      <AttachmentsSection parent={{ propertyId: property.id }} />
    </div>
  );
}
